<template>
	<div
		ref="floatingRef"
		class="z-9998"
	>
		<div
			class="cursor-pointer"
			@click="setShowTooltip"
		>
			<slot name="menuButton">
				<EllipsisVerticalIcon
					class="size-4 text-won-dark-gray"
				/>
			</slot>
		</div>

		<Teleport to="#menus-container">
			<div
				v-show="showFloating"
				ref="floating"
				:style="{
					...floatingStyles,
					...positionStyles,
					visibility: middlewareData?.hide?.referenceHidden
						? 'hidden'
						: 'visible'
				}"
				class="z-9999 overflow-hidden rounded bg-white shadow-float-select"
			>
				<div :class="menuOptionsWrapperClasses">
					<div
						v-for="option in menuOptions"
						:key="option.emitEvent"
						@click="() => emitHandler(option.emitEvent)"
					>
						<slot
							name="menuItem"
							:option="option"
						>
							<div
								class="flex cursor-pointer items-center gap-1 px-2.5 py-1.5 hover:bg-won-bg-blue"
							>
								<component 
									:is="option.icon"
									:class="[menuIconsColor, 'size-4']"
								/>
								<span class="text-[12px] leading-[15px] whitespace-nowrap text-won-blue">
									{{ option.actionText }}
								</span>
							</div>
						</slot>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script setup>
import { ref, toRefs, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useFloating, flip, offset, autoUpdate, hide } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { EllipsisVerticalIcon } from '@heroicons/vue/24/outline';
import { useClickOutsideIgnore } from '../../composables/useClickOutsideIgnore';

import { useMousePosition } from '../../store/mouse-position.store';


const emit = defineEmits(['event']);
const props = defineProps({
	placement: {
		type: String,
		default: 'bottom'
	},
	menuIconsColor: {
		type: String,
		default: ''
	},
	menuOptionClasses: {
		type: String,
		default: ''
	},
	showOnMousePosition: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	menuOptions: {
		type: Array,
		default: () => []
	},
	menuRef: {
		type: Object,
		default: null
	},
	menuOptionsWrapperClasses: {
		type: String,
		default: ''
	}
});

const { placement, showOnMousePosition, disabled } = toRefs(props);

const emitHandler = (emitEvent) => {
	emit('event', emitEvent);
};

const mousePositionStore = useMousePosition();
const { posX, posY } = storeToRefs(mousePositionStore);
const { addIgnoreEl } = useClickOutsideIgnore();

const floatingRef = ref(null);
const floating = ref(null);
const showFloating = ref(false);

onMounted(() => {
	addIgnoreEl(floating);
});

const { floatingStyles, middlewareData } = useFloating(floatingRef, floating, {
	placement: placement.value,
	middleware: [flip(), offset(4), hide()],
	whileElementsMounted: autoUpdate
});

const setShowTooltip = () => {
	if (disabled.value) {return;}
	showFloating.value = true;
};

const hideMenu = () => {
	if (disabled.value) {return;}
	showFloating.value = false;
};

onClickOutside(floatingRef, hideMenu);

const positionStyles = showOnMousePosition.value ? { top: `${posX}px`, left: `${posY}px` } : {};
</script>
